/**
 * TriggerSuggestionCard
 * Rendered when the AI suggests setting up an automation trigger.
 * Shows the trigger details with options to confirm, edit, or dismiss.
 */
import { useMemo, useState } from 'react'
import { Zap, Mail, Clock, CheckCircle, X, ChevronDown, ChevronUp, Settings, Radar, BellRing, Send, FileText, Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useRecurringTasks } from '@/hooks/useRecurringTasks'
import { buildCadenceSummary, type RecurringTaskProposal } from '@/features/recurring/workflow'
import './trigger-suggestion-card.css'

export interface TriggerSuggestionData {
  trigger_type: 'gmail' | 'schedule' | 'webhook' | 'manual'
  name: string
  description: string
  // Gmail-specific
  gmail_filter?: {
    from?: string
    subject_contains?: string
    has_attachment?: boolean
    label?: string
  }
  // Schedule-specific
  schedule?: {
    cron?: string
    human_readable?: string
  }
  workflow_spec?: {
    kind: 'monitor' | 'report' | 'reminder' | 'sync'
    checks?: string[]
    alert_conditions?: string[]
    destinations?: string[]
    rollup?: string
  }
  recurring_task?: {
    title: string
    description?: string
    category: string
    frequency: 'daily' | 'weekly' | 'monthly' | 'custom'
    custom_days?: number[]
    day_of_week?: number
    day_of_month?: number
    time_of_day: string
    connector_id?: string
    connector_name?: string
    agent_prompt: string
    ends_at?: string
    workflow_spec?: TriggerSuggestionData['workflow_spec']
  }
  // Action to perform when triggered
  action: {
    type: 'agent_run' | 'send_email' | 'notify' | 'custom'
    description: string
    prompt?: string
    reply_template?: string
  }
  // Suggested project to attach to
  project_id?: string
  project_name?: string
}

interface TriggerSuggestionCardProps {
  data: TriggerSuggestionData
  onConfirm?: (data: TriggerSuggestionData) => void
  onDismiss?: () => void
}

const TRIGGER_ICONS: Record<string, React.ReactNode> = {
  gmail: <Mail size={16} />,
  schedule: <Clock size={16} />,
  webhook: <Zap size={16} />,
  manual: <Settings size={16} />,
}

const TRIGGER_LABELS: Record<string, { no: string; color: string }> = {
  gmail: { no: 'Gmail-trigger', color: '#EA4335' },
  schedule: { no: 'Tidsplan', color: '#1A93FE' },
  webhook: { no: 'Webhook', color: '#7B5EA7' },
  manual: { no: 'Manuell', color: '#6B7280' },
}

const WORKFLOW_LABELS: Record<NonNullable<TriggerSuggestionData['workflow_spec']>['kind'], { no: string; color: string; icon: React.ReactNode }> = {
  monitor: { no: 'Monitor', color: '#EAB308', icon: <Radar size={16} /> },
  report: { no: 'Rapportflyt', color: '#10B981', icon: <FileText size={16} /> },
  reminder: { no: 'Påminnelse', color: '#8B5CF6', icon: <BellRing size={16} /> },
  sync: { no: 'Synk', color: '#06B6D4', icon: <Send size={16} /> },
}

function inferCategory(data: TriggerSuggestionData): string {
  if (data.recurring_task?.category) return data.recurring_task.category
  switch (data.workflow_spec?.kind) {
    case 'monitor':
      return 'Monitorering'
    case 'report':
      return 'Rapportering'
    case 'reminder':
      return 'Påminnelser'
    case 'sync':
      return 'Synkronisering'
    default:
      return 'Automatisering'
  }
}

function deriveRecurringTask(data: TriggerSuggestionData): RecurringTaskProposal | null {
  if (data.recurring_task) {
    return {
      title: data.recurring_task.title,
      description: data.recurring_task.description || data.description,
      category: data.recurring_task.category,
      frequency: data.recurring_task.frequency,
      customDays: data.recurring_task.custom_days,
      dayOfWeek: data.recurring_task.day_of_week,
      dayOfMonth: data.recurring_task.day_of_month,
      timeOfDay: data.recurring_task.time_of_day,
      connectorId: data.recurring_task.connector_id,
      connectorName: data.recurring_task.connector_name,
      agentPrompt: data.recurring_task.agent_prompt,
      endsAt: data.recurring_task.ends_at,
      workflowSpec: data.recurring_task.workflow_spec
        ? {
            kind: data.recurring_task.workflow_spec.kind,
            objective: data.recurring_task.description || data.description,
            checks: data.recurring_task.workflow_spec.checks,
            alertConditions: data.recurring_task.workflow_spec.alert_conditions,
            destinations: data.recurring_task.workflow_spec.destinations,
            rollup: data.recurring_task.workflow_spec.rollup,
          }
        : undefined,
    }
  }

  if (data.trigger_type !== 'schedule' || !data.schedule || !data.action.prompt) return null

  const cron = data.schedule.cron?.trim()
  const humanReadable = data.schedule.human_readable?.trim()
  let frequency: RecurringTaskProposal['frequency'] = 'weekly'
  let dayOfWeek: number | undefined
  let dayOfMonth: number | undefined
  let timeOfDay = '09:00'

  if (cron) {
    const parts = cron.split(/\s+/)
    if (parts.length >= 5) {
      const [minute, hour, dayOfMonthPart, , dayOfWeekPart] = parts
      if (/^\d+$/.test(hour) && /^\d+$/.test(minute)) {
        timeOfDay = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
      }
      if (dayOfMonthPart !== '*' && /^\d+$/.test(dayOfMonthPart)) {
        frequency = 'monthly'
        dayOfMonth = Number(dayOfMonthPart)
      } else if (dayOfWeekPart !== '*') {
        frequency = 'weekly'
        const normalizedDow = Number(dayOfWeekPart.split(',')[0])
        if (Number.isFinite(normalizedDow)) {
          dayOfWeek = normalizedDow === 0 ? 6 : Math.max(0, Math.min(6, normalizedDow - 1))
        }
      } else {
        frequency = 'daily'
      }
    }
  } else if (humanReadable) {
    const lowered = humanReadable.toLowerCase()
    const timeMatch = humanReadable.match(/(\d{1,2})[:.](\d{2})/)
    if (timeMatch) {
      timeOfDay = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`
    }
    if (lowered.includes('hver dag') || lowered.includes('daily')) {
      frequency = 'daily'
    } else if (lowered.includes('hver måned') || lowered.includes('monthly')) {
      frequency = 'monthly'
      const monthMatch = lowered.match(/(\d{1,2})\./)
      if (monthMatch) dayOfMonth = Number(monthMatch[1])
    } else {
      frequency = 'weekly'
      const weekdays = ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'lordag', 'søndag', 'sondag']
      const dayIndex = weekdays.findIndex(day => lowered.includes(day))
      if (dayIndex >= 0) {
        dayOfWeek = dayIndex > 6 ? 6 : dayIndex
      }
    }
  }

  return {
    title: data.name,
    description: data.description,
    category: inferCategory(data),
    frequency,
    dayOfWeek,
    dayOfMonth,
    timeOfDay,
    agentPrompt: data.action.prompt,
    workflowSpec: data.workflow_spec
      ? {
          kind: data.workflow_spec.kind,
          objective: data.description,
          triggerLabel: buildCadenceSummary({ frequency, dayOfWeek, dayOfMonth, timeOfDay }),
          checks: data.workflow_spec.checks,
          alertConditions: data.workflow_spec.alert_conditions,
          destinations: data.workflow_spec.destinations,
          rollup: data.workflow_spec.rollup,
        }
      : undefined,
  }
}

export function TriggerSuggestionCard({ data, onConfirm, onDismiss }: TriggerSuggestionCardProps) {
  const [confirmed, setConfirmed] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [editPrompt, setEditPrompt] = useState(data.action.prompt || '')
  const [isActivating, setIsActivating] = useState(false)
  const [activationError, setActivationError] = useState<string | null>(null)
  const { user } = useAuth()
  const { createTask } = useRecurringTasks(user?.id ?? null)

  const triggerLabel = TRIGGER_LABELS[data.trigger_type] || { no: data.trigger_type, color: '#6B7280' }
  const triggerIcon = TRIGGER_ICONS[data.trigger_type] || <Zap size={16} />
  const workflowLabel = useMemo(() => {
    if (!data.workflow_spec) return null
    return WORKFLOW_LABELS[data.workflow_spec.kind]
  }, [data.workflow_spec])
  const isMonitorLike = data.workflow_spec?.kind === 'monitor'
  const recurringTask = useMemo<RecurringTaskProposal | null>(() => deriveRecurringTask(data), [data])

  const handleConfirm = async () => {
    setActivationError(null)

    if (recurringTask) {
      setIsActivating(true)
      try {
        const created = await createTask({
          ...recurringTask,
          agentPrompt: editPrompt || recurringTask.agentPrompt,
        }, 'agent', false)
        if (!created) {
          setActivationError('Kunne ikke opprette monitoren akkurat nå.')
          return
        }
        setConfirmed(true)
        return
      } catch {
        setActivationError('Kunne ikke opprette monitoren akkurat nå.')
        return
      } finally {
        setIsActivating(false)
      }
    }

    setConfirmed(true)
    onConfirm?.({ ...data, action: { ...data.action, prompt: editPrompt } })
  }

  if (confirmed) {
    return (
      <div className="trigger-card trigger-card--confirmed">
        <CheckCircle size={16} className="trigger-card-confirmed-icon" />
        <span>{recurringTask ? 'Oppgave aktivert' : 'Trigger opprettet'}: <strong>{data.name}</strong></span>
      </div>
    )
  }

  return (
    <div className="trigger-card">
      {/* Header */}
      <div className="trigger-card-header">
        <div className="trigger-card-badge" style={{ '--badge-color': triggerLabel.color } as React.CSSProperties}>
          {triggerIcon}
          <span>{triggerLabel.no}</span>
        </div>
        {workflowLabel && (
          <div className="trigger-card-badge trigger-card-badge--workflow" style={{ '--badge-color': workflowLabel.color } as React.CSSProperties}>
            {workflowLabel.icon}
            <span>{workflowLabel.no}</span>
          </div>
        )}
        <span className="trigger-card-name">{data.name}</span>
        {onDismiss && (
          <button className="trigger-card-dismiss" onClick={onDismiss} title="Avvis">
            <X size={13} />
          </button>
        )}
      </div>

      {/* Description */}
      <p className="trigger-card-desc">{data.description}</p>

      {/* Gmail filter details */}
      {data.trigger_type === 'gmail' && data.gmail_filter && (
        <div className="trigger-card-filter">
          {data.gmail_filter.from && (
            <div className="trigger-card-filter-row">
              <span className="trigger-card-filter-label">Fra:</span>
              <code className="trigger-card-filter-value">{data.gmail_filter.from}</code>
            </div>
          )}
          {data.gmail_filter.subject_contains && (
            <div className="trigger-card-filter-row">
              <span className="trigger-card-filter-label">Emne inneholder:</span>
              <code className="trigger-card-filter-value">{data.gmail_filter.subject_contains}</code>
            </div>
          )}
          {data.gmail_filter.label && (
            <div className="trigger-card-filter-row">
              <span className="trigger-card-filter-label">Label:</span>
              <code className="trigger-card-filter-value">{data.gmail_filter.label}</code>
            </div>
          )}
        </div>
      )}

      {/* Schedule details */}
      {data.trigger_type === 'schedule' && data.schedule && (
        <div className="trigger-card-filter">
          <div className="trigger-card-filter-row">
            <span className="trigger-card-filter-label">Tidspunkt:</span>
            <code className="trigger-card-filter-value">
              {data.schedule.human_readable || data.schedule.cron}
            </code>
          </div>
        </div>
      )}

      {data.workflow_spec && (
        <div className="trigger-card-workflow">
          {data.workflow_spec.checks && data.workflow_spec.checks.length > 0 && (
            <div className="trigger-card-workflow-section">
              <span className="trigger-card-workflow-title">
                {isMonitorLike ? 'Hva jeg sjekker' : 'Hva dette gjør'}
              </span>
              <ul className="trigger-card-workflow-list">
                {data.workflow_spec.checks.map((check, index) => (
                  <li key={`${check}-${index}`}>{check}</li>
                ))}
              </ul>
            </div>
          )}

          {data.workflow_spec.alert_conditions && data.workflow_spec.alert_conditions.length > 0 && (
            <div className="trigger-card-workflow-section">
              <span className="trigger-card-workflow-title">Når jeg varsler</span>
              <ul className="trigger-card-workflow-list">
                {data.workflow_spec.alert_conditions.map((condition, index) => (
                  <li key={`${condition}-${index}`}>{condition}</li>
                ))}
              </ul>
            </div>
          )}

          {data.workflow_spec.destinations && data.workflow_spec.destinations.length > 0 && (
            <div className="trigger-card-workflow-section">
              <span className="trigger-card-workflow-title">Hvor jeg leverer</span>
              <ul className="trigger-card-workflow-list">
                {data.workflow_spec.destinations.map((destination, index) => (
                  <li key={`${destination}-${index}`}>{destination}</li>
                ))}
              </ul>
            </div>
          )}

          {data.workflow_spec.rollup && (
            <div className="trigger-card-workflow-section">
              <span className="trigger-card-workflow-title">Fast oppsummering</span>
              <p className="trigger-card-workflow-rollup">{data.workflow_spec.rollup}</p>
            </div>
          )}
        </div>
      )}

      {/* Action */}
      <div className="trigger-card-action">
        <span className="trigger-card-action-label">Handling:</span>
        <span className="trigger-card-action-desc">{data.action.description}</span>
      </div>

      {/* Expandable: edit prompt */}
      {data.action.prompt && (
        <div className="trigger-card-expand">
          <button
            className="trigger-card-expand-btn"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            <span>{showDetails ? 'Skjul instruksjon' : 'Rediger instruksjon'}</span>
          </button>
          {showDetails && (
            <textarea
              className="trigger-card-prompt-edit"
              value={editPrompt}
              onChange={e => setEditPrompt(e.target.value)}
              rows={3}
              placeholder="Instruksjon til agenten..."
            />
          )}
        </div>
      )}

      {/* Project tag */}
      {data.project_name && (
        <div className="trigger-card-project">
          <span className="trigger-card-project-label">Prosjekt:</span>
          <span className="trigger-card-project-name">{data.project_name}</span>
        </div>
      )}

      {activationError && (
        <div className="trigger-card-error">
          {activationError}
        </div>
      )}

      {/* Actions */}
      <div className="trigger-card-actions">
        <button className="trigger-card-btn-primary" onClick={() => void handleConfirm()} disabled={isActivating}>
          {isActivating ? <Loader2 size={13} className="trigger-card-spinner" /> : <Zap size={13} />}
          {recurringTask
            ? (isMonitorLike ? 'Aktiver monitor' : 'Aktiver oppgave')
            : 'Aktiver trigger'}
        </button>
        {onDismiss && (
          <button className="trigger-card-btn-ghost" onClick={onDismiss}>
            Avvis
          </button>
        )}
      </div>
    </div>
  )
}
